import isEqual from 'lodash/isEqual';
import { memo, ReactNode, useCallback } from 'react';
import styled from 'styled-components';

import { GhostButton } from 'ui/button';
import { BUTTON_SIZES } from 'ui/button/constants';
import { colors, spacing } from 'ui/theme';
import { MemoSearchableText } from 'ui-beta/searchableText';

import { useCollapseStateContext } from './context/CollapseStateContext';
import { useTreeViewContext } from './context/TreeViewContext';
import { detectNodeType } from './detectNodeType';
import { isSimpleValue } from './isSimpleValue';
import { ComponentTypes, useGetTreeComponent } from './hooks';
import { MemoTreeLeaf } from './TreeLeaf';
import { AtomicEntry, NodeType, PossibleNodeTypes, NestedTableNodeTree, Combine } from './types';

type JsonBranchProps = {
  inArray?: boolean;
  isLast?: boolean;
  label: string;
  depth?: number;
  type?: NodeType;
  value: Exclude<PossibleNodeTypes, AtomicEntry>;
  path: string;
};

type NestedTableBranchProps = {
  depth?: number;
  value: NestedTableNodeTree[];
  color: string;
  path: string;
  row?: NonNullable<ReactNode>;
};

type TreeBranchProps = Combine<JsonBranchProps, NestedTableBranchProps>;

export const CollapseButton = styled(GhostButton)`
  height: auto;
  padding: ${spacing.eighth};
  margin-left: -${spacing.eighth};
  margin-right: ${spacing.quarter};

  path {
    stroke-width: 1.5;
  }
`;

export const TreeBranch = ({
  inArray,
  isLast,
  label,
  depth = 0,
  color = colors.white,
  value,
  type,
  path,
  row,
}: TreeBranchProps) => {
  const { highlight, sort, decompose } = useTreeViewContext();
  const { toggle, isCollapsed } = useCollapseStateContext();
  const toggleCollapse = useCallback(() => toggle(path), [path]);

  const isArray = type === NodeType.array;
  const BranchComponent = useGetTreeComponent(ComponentTypes.branch);

  const newDepth = depth + 1;

  return (
    <BranchComponent
      CollapseButton={({ children }: { children: ReactNode }) => (
        <CollapseButton
          aria-label="Collapse"
          data-test="collapse-button"
          onClick={toggleCollapse}
          size={BUTTON_SIZES.SMALL}
        >
          {children}
        </CollapseButton>
      )}
      depth={newDepth}
      inArray={inArray}
      isCollapsed={isCollapsed(path)}
      isLast={isLast}
      label={
        !inArray && typeof label === 'string' ? (
          <MemoSearchableText searchText={highlight} text={label} />
        ) : (
          label
        )
      }
      type={type}
      value={value}
      row={row}
      path={path}
      color={color}
    >
      {decompose({
        src: value,
        sort,
        color,
        depth: newDepth,
        render: ({
          isLastElement,
          label: childLabel = '',
          value: childValue,
          path: key,
          row: childRow,
          isLeaf,
          color: childColor,
        }) => {
          const valueType = detectNodeType(childValue as PossibleNodeTypes);
          const childPath = key || childLabel;

          if (isLeaf || isSimpleValue(valueType)) {
            return (
              <MemoTreeLeaf
                inArray={isArray}
                isLast={isLastElement}
                key={childPath}
                label={childLabel}
                type={valueType}
                depth={newDepth}
                value={childValue as AtomicEntry | NonNullable<ReactNode>}
                color={childColor}
              />
            );
          }

          return (
            <MemoTreeBranch
              depth={newDepth}
              inArray={isArray}
              isLast={isLastElement}
              key={childPath}
              label={childLabel}
              type={valueType}
              value={childValue as Exclude<PossibleNodeTypes, AtomicEntry> | NestedTableNodeTree[]}
              path={`${path}.${childPath}`}
              row={childRow}
              color={childColor}
            />
          );
        },
      })}
    </BranchComponent>
  );
};

export const MemoTreeBranch = memo(TreeBranch, (prev, next) => isEqual(prev, next));
